import { AsyncPipe, NgClass } from '@angular/common';
import { Component, ElementRef, inject, model, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatBadge } from '@angular/material/badge';
import { MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { userEnums, webDtos } from '@vectronic/openApi-customer';
import { AlertComponent, CardComponent, ParameterViewComponent } from '@vectronic/ui';
import { CollarAndGroup, DropOffDataTable } from '@vectronic/util';
import {
  REMOTE_COMMAND_MODAL_SERVICE,
  RemoteCommandModalService,
} from '../../services/commands/remote-command-modal.service';
import { DropoffScheduleComponent } from '../dropoff-schedule/dropoff-schedule.component';
import { ScheduleViewComponent } from '../schedule-view/schedule-view.component';

@Component({
  selector: 'rc-generic-remote-commands-modal',
  templateUrl: './generic-remote-commands-modal.component.html',
  styleUrls: ['./generic-remote-commands-modal.component.scss'],
  imports: [
    AsyncPipe,
    NgClass,
    AlertComponent,
    ParameterViewComponent,
    ScheduleViewComponent,
    CardComponent,
    MatBadge,
    MatFormField,
    MatChipGrid,
    MatChipRow,
    MatTooltip,
    MatIcon,
    MatChipRemove,
    ReactiveFormsModule,
    FormsModule,
    MatChipInput,
    MatAutocompleteModule,
    MatOptionModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatIconButton,
    MatProgressBar,
    MatProgressSpinner,
    DropoffScheduleComponent,
  ],
})
export class GenericRemoteCommandsModalComponent implements OnInit, OnDestroy {
  private vm = inject(REMOTE_COMMAND_MODAL_SERVICE) as RemoteCommandModalService;

  get result(): webDtos.CustomerCommand[] | undefined {
    return this.vm.dialogResult;
  }

  collarInput = viewChild<ElementRef<HTMLInputElement>>('collarInput');
  matAutocomplete = viewChild(MatAutocompleteTrigger);

  remoteCommandsService = this.vm.remoteCommandsService;
  validationService = this.vm.validationService;
  remoteCommandProviderService = this.vm.remoteCommandProviderService;
  scheduleServices = this.vm.scheduleServices;
  camerasFormatService = this.vm.camerasFormatService;
  parametersIdentifierService = this.vm.parametersIdentifierService;
  dropOffCommandService = this.vm.dropOffCommandService;
  dialogRef = this.vm.dialogRef;
  data = this.vm.data;

  isFullScreen = this.vm.isFullScreen;
  isConfirmed = this.vm.isConfirmed;
  isLoading = this.vm.isLoading;
  selectedTab = this.vm.selectedTab;
  showPreview = model<boolean>(true);
  showUserOrFactoryToggle = this.vm.showUserOrFactoryToggle;
  showMore = this.vm.showMore;
  collarCtrl = this.vm.collarCtrl;
  separatorKeysCodes = this.vm.separatorKeysCodes;
  selectedCollarsAndGroups$ = this.vm.selectedCollarsAndGroups$;
  canSendCommands = this.vm.canSendCommands;
  filteredCollars$ = this.vm.filteredCollars$;

  filteredParameters$ = this.vm.filteredParameters$;
  categories$ = this.vm.categories$;
  subsystems$ = this.vm.subsystems$;
  customCategoryCounts$ = this.vm.customCategoryCounts$;
  videoFormatsOptions$ = this.vm.videoFormatsOptions$;
  photoFormatsOptions$ = this.vm.photoFormatsOptions$;
  permanentEndDate$ = this.vm.permanentEndDate$;
  permanentEndDateByCollar$ = this.vm.permanentEndDateByCollar$;
  allDropOffIds$ = this.vm.allDropOffIds$;

  deviceCommandResponse = this.vm.deviceCommandResponse;
  virtualFenceNotAvailableForIds = this.vm.virtualFenceNotAvailableForIds;
  hasMultipleCameraTypes = this.vm.hasMultipleCameraTypes;
  userSubsystemVisibility = this.vm.userSubsystemVisibility;
  warningByParameterId = this.vm.warningByParameterId;
  errorByParameterId = this.vm.errorByParameterId;
  disabledByParameterId = this.vm.disabledByParameterId;
  isChanged = this.vm.isChanged;
  isFilled = this.vm.isFilled;
  configCategoryTitles = this.vm.configCategoryTitles;
  userOrFactory = this.vm.userOrFactory;

  isPositionsLoading = this.vm.isPositionsLoading;
  mapPositions = this.vm.mapPositions;
  mapPreferences = this.vm.mapPreferences;
  userAccess = this.vm.userAccess;

  get isAdmin() {
    return this.vm.isAdmin;
  }

  get virtualFenceRules() {
    return this.vm.virtualFenceRules;
  }
  get isSendEnabled() {
    return this.vm.isSendEnabled;
  }

  getTitle = (selectedCollars: CollarAndGroup[]) => this.vm.getTitle(selectedCollars);
  sendTooltip = () => this.vm.sendTooltip();
  collarsWithShortHeaderFormatJoin = () => this.vm.collarsWithShortHeaderFormatJoin();
  getTooltip = (collar: CollarAndGroup) => this.vm.getTooltip(collar);

  onClickedSelectCollar = () => this.vm.onClickedSelectCollar();
  onRemoveCollarAndGroup = (collar: CollarAndGroup) => this.vm.onRemoveCollarAndGroup(collar);
  add = () => this.vm.add();
  onKeyUp = (event: KeyboardEvent) => this.vm.onKeyUp(event);
  onPaste = (event: ClipboardEvent) => this.vm.onPaste(event);
  onChangeBasicOrAdvanced = ($event: 'basic' | 'advanced') =>
    this.vm.onChangeBasicOrAdvanced($event);
  onChangeUserOrFactory = ($event: 'customer' | 'allUser' | 'allFactory') =>
    this.vm.onChangeUserOrFactory($event);
  onChangeCategory = ($event: string) => this.vm.onChangeCategory($event);
  onClear = () => this.vm.onClear();
  resetToDefaultPolygons = () => this.vm.resetToDefaultPolygons();
  onSendRemoteCommand = () => this.vm.onSendRemoteCommand();
  onResized = (isFullScreen: boolean) => this.vm.onResized(isFullScreen);
  onClose = () => this.vm.onClose();
  onLoadPositions = (request: any) => this.vm.onLoadPositions(request);
  onCollarSelected = (event: any) => this.vm.onCollarSelected(event);
  onAddShare = ($event: number) => this.vm.onAddShare($event);
  onEditShare = ($event: { idDevice: number; email: string; oldRole: userEnums.Role }) =>
    this.vm.onEditShare($event);
  onDeleteShare = ($event: { idDevice: number; email: string }) => this.vm.onDeleteShare($event);
  onAddDropOff = ($event: { row: DropOffDataTable; dropOffId: string }) =>
    this.vm.onAddDropOff($event);
  onRemove = ($event: DropOffDataTable) => this.vm.onRemove($event);
  onSubmitFireDropOff = () => this.vm.onSubmitFireDropOff();
  setSelectedIds = (ids: number[]) => this.vm.setSelectedIds(ids);

  ngOnInit() {
    this.vm.collarInputRef = () => this.collarInput()?.nativeElement;
    this.vm.autocompleteTriggerRef = () => this.matAutocomplete();
    this.vm.init();
  }

  ngOnDestroy() {
    this.vm.destroy();
  }
}
